import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Megaphone, Trophy, HandHeart, Sparkles, Trash2 } from "lucide-react";

interface Props {
  ideaId: string;
  ownerId: string;
}

const TYPE_META: Record<string, { label: string; icon: any; className: string }> = {
  progress: { label: "Progress", icon: Megaphone, className: "bg-secondary text-secondary-foreground" },
  milestone: { label: "Milestone", icon: Sparkles, className: "bg-primary/15 text-primary border border-primary/30" },
  ask: { label: "Ask", icon: HandHeart, className: "bg-accent text-accent-foreground" },
  win: { label: "Win", icon: Trophy, className: "bg-primary/15 text-primary border border-primary/30" },
};

export default function IdeaUpdatesTab({ ideaId, ownerId }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isOwner = user?.id === ownerId;
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [updateType, setUpdateType] = useState<"progress" | "milestone" | "ask" | "win">("progress");
  const [posting, setPosting] = useState(false);

  const { data: updates, isLoading } = useQuery({
    queryKey: ["idea-updates", ideaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("idea_updates")
        .select("*")
        .eq("idea_id", ideaId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const post = async () => {
    if (!user || !title.trim() || !body.trim()) return;
    setPosting(true);
    const { error } = await supabase.from("idea_updates").insert({
      idea_id: ideaId,
      author_user_id: user.id,
      title: title.trim(),
      body: body.trim(),
      update_type: updateType,
    });
    setPosting(false);
    if (error) {
      toast.error(error.message || "Failed to post update");
      return;
    }
    toast.success("Update posted — followers notified");
    setTitle("");
    setBody("");
    setUpdateType("progress");
    qc.invalidateQueries({ queryKey: ["idea-updates", ideaId] });
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("idea_updates").delete().eq("id", id);
    if (error) toast.error("Failed to delete");
    else {
      toast.success("Update removed");
      qc.invalidateQueries({ queryKey: ["idea-updates", ideaId] });
    }
  };

  return (
    <div className="space-y-6">
      {isOwner && (
        <Card className="p-5 space-y-3">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-foreground">
            Post an update
          </h3>
          <Select value={updateType} onValueChange={(v: any) => setUpdateType(v)}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="progress">Progress</SelectItem>
              <SelectItem value="milestone">Milestone</SelectItem>
              <SelectItem value="ask">Ask</SelectItem>
              <SelectItem value="win">Win</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Update title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={140}
          />
          <Textarea
            placeholder="What happened? What's next? Any asks for the community?"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            maxLength={4000}
          />
          <div className="flex justify-end">
            <Button onClick={post} disabled={posting || !title.trim() || !body.trim()}>
              {posting ? "Posting..." : "Post update"}
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading updates...</div>
      ) : !updates || updates.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No updates yet. {isOwner ? "Post the first one above." : "Follow this project to be notified when there is news."}
        </Card>
      ) : (
        <div className="space-y-4">
          {updates.map((u: any) => {
            const meta = TYPE_META[u.update_type] ?? TYPE_META.progress;
            const Icon = meta.icon;
            return (
              <Card key={u.id} className="p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className={meta.className + " gap-1"}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(u.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  {isOwner && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
                <h4 className="font-semibold text-foreground mb-1">{u.title}</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {u.body}
                </p>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}